import { useEffect, useState, type RefObject } from 'react';

export type SpokePath = {
  id: string;
  d: string;
  side: 'left' | 'right' | 'bottom';
  index: number;
};

interface SpokeRefs {
  wrapperRef: RefObject<HTMLDivElement | null>;
  centerRef: RefObject<HTMLDivElement | null>;
  problemRefs: RefObject<(HTMLDivElement | null)[]>;
  promiseRefs: RefObject<(HTMLDivElement | null)[]>;
  bottomRef: RefObject<HTMLDivElement | null>;
}

function relativeRect(el: HTMLElement, wrapper: HTMLElement) {
  const wr = wrapper.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  return {
    left: er.left - wr.left,
    top: er.top - wr.top,
    width: er.width,
    height: er.height,
  };
}

function bezier(x1: number, y1: number, x2: number, y2: number): string {
  const dx = Math.abs(x2 - x1) * 0.4;
  const cx1 = x1 + (x2 > x1 ? dx : -dx);
  const cx2 = x2 + (x2 > x1 ? -dx : dx);
  return `M ${x1},${y1} C ${cx1},${y1} ${cx2},${y2} ${x2},${y2}`;
}

function verticalBezier(x1: number, y1: number, x2: number, y2: number): string {
  const dy = Math.abs(y2 - y1) * 0.4;
  return `M ${x1},${y1} C ${x1},${y1 + dy} ${x2},${y2 - dy} ${x2},${y2}`;
}

export function useSpokePositions(refs: SpokeRefs): SpokePath[] | null {
  const [paths, setPaths] = useState<SpokePath[] | null>(null);

  useEffect(() => {
    const wrapper = refs.wrapperRef.current;
    if (!wrapper) return;

    function measure() {
      const w = refs.wrapperRef.current;
      const hub = refs.centerRef.current;
      const bottom = refs.bottomRef.current;
      if (!w || !hub || !bottom) return;

      const hubR = relativeRect(hub, w);
      const hubCx = hubR.left + hubR.width / 2;
      const hubCy = hubR.top + hubR.height / 2;
      const result: SpokePath[] = [];

      const problems = refs.problemRefs.current;
      if (problems) {
        problems.forEach((el, i) => {
          if (!el) return;
          const r = relativeRect(el, w);
          const sx = r.left + r.width;
          const sy = r.top + r.height / 2;
          const ex = hubR.left;
          const ey = hubCy;
          result.push({ id: `left-${i}`, d: bezier(sx, sy, ex, ey), side: 'left', index: i });
        });
      }

      const promises = refs.promiseRefs.current;
      if (promises) {
        promises.forEach((el, i) => {
          if (!el) return;
          const r = relativeRect(el, w);
          const sx = r.left;
          const sy = r.top + r.height / 2;
          const ex = hubR.left + hubR.width;
          const ey = hubCy;
          result.push({ id: `right-${i}`, d: bezier(sx, sy, ex, ey), side: 'right', index: i });
        });
      }

      const bR = relativeRect(bottom, w);
      result.push({
        id: 'bottom',
        d: verticalBezier(hubCx, hubR.top + hubR.height, hubCx, bR.top),
        side: 'bottom',
        index: 0,
      });

      setPaths(result);
    }

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [refs]);

  return paths;
}
